export function joinScheduleDateTime(date: string, time: string): Date | null {
  if (!date?.trim() || !time?.trim()) return null;

  const [year, month, day] = date.split("-").map((part) => Number.parseInt(part, 10));
  const [hours, minutes] = time.split(":").map((part) => Number.parseInt(part, 10));

  if (
    [year, month, day, hours, minutes].some((part) => Number.isNaN(part)) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return null;
  }

  return new Date(year, month - 1, day, hours, minutes, 0, 0);
}

export function splitScheduleDateTime(value: Date | null | undefined): {
  date: string;
  time: string;
} {
  if (!value || Number.isNaN(value.getTime())) {
    return { date: "", time: "" };
  }

  const pad = (part: number) => String(part).padStart(2, "0");

  return {
    date: `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`,
    time: `${pad(value.getHours())}:${pad(value.getMinutes())}`,
  };
}

export function startOfToday(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

export function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Earliest allowed schedule slot (now, rounded up to the next minute). */
export function getEarliestSchedulableDateTime(): Date {
  const now = Date.now();
  const earliest = new Date(now);
  earliest.setSeconds(0, 0);
  earliest.setMilliseconds(0);

  if (earliest.getTime() < now) {
    earliest.setMinutes(earliest.getMinutes() + 1);
  }

  return earliest;
}

export function isScheduleDateTimeInPast(value: Date | null | undefined): boolean {
  if (!value || Number.isNaN(value.getTime())) return false;
  return value.getTime() < getEarliestSchedulableDateTime().getTime();
}

export function clampScheduleDateTime(value: Date): Date {
  return isScheduleDateTimeInPast(value) ? getEarliestSchedulableDateTime() : value;
}

function withScheduleTime(base: Date, hour: number, minute: number): Date {
  const slot = new Date(base);
  slot.setHours(hour, minute, 0, 0);
  return slot;
}

export function shouldDisableScheduleDate(candidate: Date): boolean {
  const day = new Date(candidate);
  day.setHours(0, 0, 0, 0);
  return day < startOfToday();
}

export function shouldDisableScheduleHour(hour: number, candidate: Date): boolean {
  const earliest = getEarliestSchedulableDateTime();
  if (!isSameCalendarDay(candidate, earliest)) return false;

  return withScheduleTime(candidate, hour, 59).getTime() < earliest.getTime();
}

export function shouldDisableScheduleMinute(minute: number, candidate: Date): boolean {
  const earliest = getEarliestSchedulableDateTime();
  if (!isSameCalendarDay(candidate, earliest)) return false;

  return withScheduleTime(candidate, candidate.getHours(), minute).getTime() < earliest.getTime();
}

/** Class on RSuite DatePicker menu — used to find the open dropdown in the DOM. */
export const SCHEDULE_DATE_TIME_MENU_CLASS = "sched-date-time-menu";

/**
 * RSuite datetime picker shows the calendar first; time is a separate panel.
 * After the user picks a day, switch to the time panel automatically.
 */
export function openRsuiteDatePickerTimeView(menuRoot: ParentNode | null | undefined): void {
  if (!menuRoot) return;

  const calendar = menuRoot.querySelector(".rs-calendar");
  if (!calendar || calendar.classList.contains("rs-calendar-time-view")) return;

  menuRoot.querySelector<HTMLButtonElement>(".rs-calendar-header-title-time")?.click();
}
